import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createVNode as _createVNode } from "vue";
const _hoisted_1 = { class: "accommodation-page container py-4" };
const _hoisted_2 = {
    key: 0,
    class: "loading-state"
};
const _hoisted_3 = {
    key: 1,
    class: "error-state"
};
const _hoisted_4 = { key: 2 };
const _hoisted_5 = {
    key: 3,
    class: "empty-state"
};
import { ref, onMounted, watch, computed, getCurrentInstance } from 'vue';
import { useRoute } from 'vue-router';
import AccommodationHero from '@/components/accommodation/AccommodationHero.vue';
import AccommodationHeader from '@/components/accommodation/AccommodationHeader.vue';
import AccommodationDetails from '@/components/accommodation/AccommodationDetails.vue';
import AccommodationMap from '@/components/accommodation/AccommodationMap.vue';
import AccommodationNearbyCarousel from '@/components/accommodation/AccommodationNearbyCarousel.vue';
import AmenitiesSection from '@/components/accommodation/AmenitiesSection.vue';
import RoomsRatesSection from '@/components/accommodation/RoomsRatesSection.vue';
import ReviewsSection from '@/components/accommodation/ReviewsSection.vue';
import PoliciesSection from '@/components/accommodation/PoliciesSection.vue';
import HowToArriveSection from '@/components/accommodation/HowToArriveSection.vue';
import StickyCTA from '@/components/accommodation/StickyCTA.vue';
import { fetchAccommodations } from '@/services/accommodations.service';
// Props para recibir el ID del hotel desde la ruta
export default /*@__PURE__*/ _defineComponent({
    __name: 'AccommodationPage',
    props: {
        id: {}
    },
    setup(__props) {
        const props = __props;
        const route = useRoute();
        const app = getCurrentInstance();
        const currentAccommodation = ref(null);
        const loading = ref(false);
        const error = ref(null);
        // Función para obtener el ID del hotel (desde props o route params)
        const getAccommodationId = () => {
            return props.id || route.params.id;
        };
        // Función para obtener la imagen principal
        const getMainImage = (accommodation) => {
            const buildImg = app?.appContext?.config?.globalProperties?.$buildImg;
            const mainImage = accommodation.images?.find(img => img.is_main);
            if (mainImage) {
                return buildImg ? buildImg(mainImage.image_url) : mainImage.image_url;
            }
            if (accommodation.images && accommodation.images.length > 0) {
                const url = accommodation.images[0].image_url;
                return buildImg ? buildImg(url) : url;
            }
            return 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=600&h=400&fit=crop';
        };
        // Normalizar y formatear el precio
        const formatPrice = (price) => {
            const numPrice = Math.abs(parseFloat(price));
            if (isNaN(numPrice))
                return 'Consultar precio';
            return `$${numPrice.toFixed(0)}/noche`;
        };
        // Datos derivados para nuevas secciones (placeholder a partir de datos disponibles)
        const computedAmenities = computed(() => {
            if (!currentAccommodation.value)
                return [];
            const base = [
                { title: 'Generales', items: ['WiFi', 'Desayuno incluido', 'Recepción 24h'] },
                { title: 'Habitación', items: ['Baño privado', 'TV', 'Ropa de cama'] },
                { title: 'Seguridad', items: ['Extintor', 'Detectores de humo'] }
            ];
            return base;
        });
        const computedRooms = computed(() => {
            if (!currentAccommodation.value)
                return [];
            // Si la API no provee rooms, construimos 8 ejemplos a partir del precio base
            const base = Math.max(0, Math.abs(parseFloat(currentAccommodation.value.price_per_night || '0')));
            const img = getMainImage(currentAccommodation.value);
            const capacities = [2, 2, 3, 4, 2, 3, 4, 5];
            const names = ['Estándar', 'Doble', 'Triple', 'Familiar', 'Superior', 'Deluxe', 'Suite Junior', 'Suite Master'];
            const beds = ['1 cama doble', '2 camas individuales', '1 doble + 1 individual', '2 dobles', '1 queen', '1 king', '1 king + sofá cama', '2 queen'];
            return names.map((n, idx) => ({
                id: `${currentAccommodation.value.id}-r${idx + 1}`,
                name: `Habitación ${n}`,
                capacity: capacities[idx] || 2,
                beds: beds[idx] || '1 cama',
                amenities: ['WiFi', 'Baño privado', 'TV'],
                photos: [img],
                pricePerNight: String(base + idx * 15)
            }));
        });
        const computedTransport = computed(() => {
            return ['A 15 min del centro en taxi', 'Línea de bus 3 a 2 calles'];
        });
        const computedPOIs = computed(() => {
            if (!currentAccommodation.value?.place?.name)
                return [];
            return [
                { name: `Plaza principal de ${currentAccommodation.value.place.name}`, distanceKm: 1.2 },
                { name: 'Terminal de buses', distanceKm: 2.8 }
            ];
        });
        const computedReviews = computed(() => {
            return [
                { id: 1, author: 'María', rating: 5, date: new Date().toISOString(), text: 'Excelente atención y limpio.' },
                { id: 2, author: 'Jorge', rating: 4, date: new Date().toISOString(), text: 'Buena ubicación, volvería.' }
            ];
        });
        const computedReviewSummary = computed(() => {
            const list = computedReviews.value;
            if (!list.length)
                return { average: 0, count: 0 };
            const avg = list.reduce((a, b) => a + b.rating, 0) / list.length;
            return { average: avg, count: list.length };
        });
        const computedPolicies = computed(() => {
            return { checkIn: '14:00', checkOut: '11:00', cancellation: 'Cancelación gratuita 48h antes', children: 'Se permiten niños', pets: 'Consultar' };
        });
        const computedPayments = computed(() => {
            return ['Efectivo', 'Tarjeta de crédito', 'Transferencia'];
        });
        // Función para cargar el hotel específico
        const fetchAccommodation = async () => {
            const accommodationId = getAccommodationId();
            if (!accommodationId) {
                error.value = 'ID de hotel no proporcionado';
                return;
            }
            loading.value = true;
            error.value = null;
            try {
                const accommodations = await fetchAccommodations();
                const accommodation = accommodations.find(acc => acc.id.toString() === accommodationId);
                if (accommodation) {
                    currentAccommodation.value = accommodation;
                }
                else {
                    throw new Error('Hotel no encontrado');
                }
            }
            catch (err) {
                console.error('Error fetching accommodation:', err);
                error.value = 'Error al cargar el hotel';
                currentAccommodation.value = null;
            }
            finally {
                loading.value = false;
            }
        };
        // Cargar hotel al montar el componente
        onMounted(() => {
            fetchAccommodation();
        });
        // Observar cambios en el ID del hotel
        watch(() => getAccommodationId(), () => {
            fetchAccommodation();
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("main", _hoisted_1, [
                (loading.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, _cache[0] || (_cache[0] = [
                        _createElementVNode("div", { class: "spinner" }, null, -1),
                        _createElementVNode("p", null, "Cargando hotel...", -1)
                    ])))
                    : (error.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                            _cache[1] || (_cache[1] = _createElementVNode("i", { class: "fas fa-exclamation-triangle" }, null, -1)),
                            _createElementVNode("p", null, _toDisplayString(error.value), 1),
                            _createElementVNode("button", {
                                onClick: fetchAccommodation,
                                class: "retry-btn"
                            }, "Reintentar")
                        ]))
                        : (currentAccommodation.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                _createVNode(AccommodationHero, {
                                    featured: {
                                        ...currentAccommodation.value,
                                        regionImage: getMainImage(currentAccommodation.value)
                                    }
                                }, null, 8, ["featured"]),
                                _createVNode(AccommodationHeader, {
                                    title: currentAccommodation.value.title,
                                    subtitle: currentAccommodation.value.description,
                                    price: formatPrice(currentAccommodation.value.price_per_night),
                                    location: currentAccommodation.value.location,
                                    badge: currentAccommodation.value.badge
                                }, null, 8, ["title", "subtitle", "price", "location", "badge"]),
                                _createVNode(AmenitiesSection, { amenities: computedAmenities.value }, null, 8, ["amenities"]),
                                _createVNode(AccommodationDetails, { accommodation: currentAccommodation.value }, null, 8, ["accommodation"]),
                                _createVNode(RoomsRatesSection, {
                                    rooms: computedRooms.value,
                                    "hotel-name": currentAccommodation.value.title,
                                    "phone-or-whatsapp": currentAccommodation.value.whatsapp || null,
                                    "page-size": 6
                                }, null, 8, ["rooms", "hotel-name", "phone-or-whatsapp"]),
                                _createVNode(AccommodationMap, { accommodation: currentAccommodation.value }, null, 8, ["accommodation"]),
                                _createVNode(HowToArriveSection, {
                                    latitude: currentAccommodation.value.latitude,
                                    longitude: currentAccommodation.value.longitude,
                                    address: currentAccommodation.value.place?.address || null,
                                    transport: computedTransport.value,
                                    "points-of-interest": computedPOIs.value
                                }, null, 8, ["latitude", "longitude", "address", "transport", "points-of-interest"]),
                                _createVNode(ReviewsSection, {
                                    reviews: computedReviews.value,
                                    summary: computedReviewSummary.value
                                }, null, 8, ["reviews", "summary"]),
                                _createVNode(PoliciesSection, {
                                    policies: computedPolicies.value,
                                    payments: computedPayments.value
                                }, null, 8, ["policies", "payments"]),
                                _createVNode(AccommodationNearbyCarousel, {
                                    excludeAccommodationId: currentAccommodation.value.id
                                }, null, 8, ["excludeAccommodationId"]),
                                _createVNode(StickyCTA, {
                                    title: currentAccommodation.value.title,
                                    price: formatPrice(currentAccommodation.value.price_per_night),
                                    "phone-or-whatsapp": currentAccommodation.value.whatsapp || null
                                }, null, 8, ["title", "price", "phone-or-whatsapp"])
                            ]))
                            : (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[2] || (_cache[2] = [
                                _createElementVNode("i", { class: "fas fa-bed" }, null, -1),
                                _createElementVNode("p", null, "Hotel no encontrado", -1)
                            ])))
            ]));
        };
    }
});
